import * as Sentry from '@sentry/browser'
import Agent from 'agentkeepalive'
import axios, { AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'

import { getVersion } from '@/api/version'
import AddonService from '@/modules/addon/services/AddonService'
import FloorplanService from '@/modules/floorplan/services/FloorplanService'
import GuestService from '@/modules/guest/services/GuestService'
import MailchimpService from '@/modules/mailchimp/services/MailchimpService'
import NotificationTemplateService from '@/modules/notification/services/NotificationTemplateService'
import PerformanceService from '@/modules/performance/services/PerformanceService'
import CouponService from '@/modules/promoCode/services/CouponService'
import PromoCodeService from '@/modules/promoCode/services/PromoCodeService'
import AccountingService from '@/modules/report/services/AccountingService'
import ReportService from '@/modules/report/services/ReportService'
import ReservationService from '@/modules/reservation/services/ReservationService'
import ResidencyService from '@/modules/residency/services/ResidencyService'
import SettingService from '@/modules/setting/services/SettingService'
import ShowService from '@/modules/show/services/ShowService'
import SeatingTimeService from '@/modules/showtime/services/SeatingTimeService'
import ShowtimeService from '@/modules/showtime/services/ShowtimeService'
import StripeService from '@/modules/stripe/services/StripeService'
import UserService from '@/modules/user/services/UserService'
import { useSettingStore } from '@/stores/setting'
import { enhanceError, isErrorData } from '@/utils/errors'
import loc from '@/utils/loc'
import { authRedirect } from '@/utils/utils'

function isErrorResponse(response: AxiosResponse): boolean {
  return response.data.error && isErrorData(response.data.error)
}

export class UnauthenticatedError extends Error {}
export class ServerFailureError extends Error {}
export class BadRequestError extends Error {}
export class BackendAPIError extends Error {}
export class NoResponseError extends Error {}
export class InvalidVenueError extends Error {}

export async function handleAxiosError(error) {
  if (!axios.isAxiosError(error)) {
    Sentry.captureException(new Error('Axios had an exception not an AxiosError', { cause: error }))
  } else {
    Sentry.setContext('axiosConfig', error.config)
    Sentry.setContext('axiosRequest', error.request || {})
    if (!error.response) {
      Sentry.captureException(
        new NoResponseError(`Recieved no response in api call to ${error.config.url}`, {
          cause: error,
        }),
      )
    } else {
      const status_code = error.response.status
      Sentry.setContext('axiosResponse', error.response)
      if (!isErrorResponse(error.response)) {
        Sentry.captureException(
          new ServerFailureError(
            `Received ill-formated ${status_code} response from ${error.config.url}`,
            { cause: error },
          ),
        )
      } else {
        Sentry.setContext('ttError', error.response.data.error)

        // Standardize our handling of API responses so code above can easily tell the difference between
        // the BE rejecting an invalid request and something going wrong in typescript code,
        error = enhanceError(error, error.response.data.error)
        if (status_code == 400) {
          Sentry.captureException(
            new BadRequestError(`${error.code} in ${error.config.url}`, { cause: error }),
          )
        } else if (status_code == 401 || status_code == 403) {
          const location = loc.location()
          return authRedirect(location.pathname + location.search)
        } else if (status_code >= 500) {
          Sentry.captureException(
            new ServerFailureError(`Recieved ${status_code} in api call to ${error.config.url}`, {
              cause: error,
            }),
          )
        } else {
          Sentry.captureException(
            new BackendAPIError(`Handled ${status_code}-${error.code} in ${error.config.url}`, {
              cause: error,
            }),
          )
        }
      }
    }
  }
  return Promise.reject(error)
}

export function configureBrowserAxios() {
  axiosRetry(axios, { retries: 2, retryDelay: axiosRetry.exponentialDelay })
  axios.interceptors.response.use(response => {
    useSettingStore().updateVenueAppVersion(response.headers['x-tt-version'])
    return response
  }, handleAxiosError)

  axios.defaults.xsrfHeaderName = 'X-CSRFToken'
  axios.defaults.xsrfCookieName = 'csrftoken'
  axios.defaults.headers['X-TT-ClientVersion'] = getVersion()
}

export function configureSsrAxiosOnce() {
  axios.defaults.httpAgent = new Agent({
    keepAlive: true,
    maxSockets: 24,
    maxTotalSockets: 24,
    maxFreeSockets: 4,
    timeout: 60000,
    freeSocketTimeout: 30000,
  })

  axios.interceptors.response.use(
    r => r,
    async error => {
      if (error.response?.status === 404) {
        throw new InvalidVenueError('Invalid Venue')
      }
      return Promise.reject(error)
    },
  )
}

export function configureSsrAxios(host: string) {
  axios.defaults.baseURL = `http://127.0.0.1/` // We're connecting directly to nginx to bypass AWS load balancer
  axios.defaults.headers.common['Host'] = host
}

export function configureDevSsrAxios(host: string) {
  axios.defaults.baseURL = host // Connect to wherever, TT_HOST=https://parker-jazz.turntabletix.com for example
}

export const getSettings = SettingService.get
export const updateSettings = SettingService.update
export const getAddOns = AddonService.fetchAll
export const updateAllAddOns = AddonService.updateAll
export const getFloorPlans = FloorplanService.fetchAll
export const addFloorPlan = FloorplanService.create
export const addFloorPlanDetailed = FloorplanService.createDetailed
export const deleteFloorPlan = FloorplanService.delete
export const updateFloorPlanDetailed = FloorplanService.updateDetailed
export const duplicateFloorPlan = FloorplanService.duplicate
export const getShowTimes = ShowtimeService.fetchAll
export const addShowTime = ShowtimeService.create
export const updateShowTime = ShowtimeService.update
export const updateAllShowTimes = ShowtimeService.updateAll
export const deleteShowTime = ShowtimeService.delete
export const addSeatingTime = SeatingTimeService.create
export const updateSeatingTime = SeatingTimeService.update
export const getShow = ShowService.fetchOne
export const getShows = ShowService.fetchAll
export const addShow = ShowService.create
export const updateShow = ShowService.update
export const updateShowImage = ShowService.updateImage
export const updateShowImagePosition = ShowService.updateImagePosition
export const updateShowCrops = ShowService.updateCrops
export const deleteShow = ShowService.delete
export const duplicateShow = ShowService.duplicate
export const getPerformances = PerformanceService.fetchAll
export const getPerformanceReports = PerformanceService.fetchReports
export const getPerformance = PerformanceService.fetchOne
export const addPerformance = PerformanceService.create
export const updatePerformance = PerformanceService.update
export const updatePerformanceDetailed = PerformanceService.updateDetailed
export const deletePerformance = PerformanceService.delete
export const getPerformanceReservations = PerformanceService.fetchReservations
export const sendMassEmail = PerformanceService.sendMassEmail
export const getAccountingSummary = AccountingService.getSummary
export const getSummaryReports = ReportService.getSummary
export const createReportInstance = ReportService.createInstance
export const getReportInstance = ReportService.getInstance
export const getReportInstances = ReportService.listInstances
export const createReportSchedule = ReportService.createSchedule
export const listReportSchedules = ReportService.listSchedules
export const updateReportSchedule = ReportService.updateSchedule
export const deleteReportSchedule = ReportService.deleteSchedule
export const getReservations = ReservationService.fetchAll
export const getReservation = ReservationService.fetchOne
export const getReservationsByPerformance = ReservationService.fetchByPerformance
export const reserveReservation = ReservationService.reserve
export const addReservation = ReservationService.create
export const retryReservation = ReservationService.retry
export const updateReservation = ReservationService.update
export const paidUpdateReservation = ReservationService.updatePaid
export const payReservation = ReservationService.pay
export const refundTransaction = ReservationService.refundTransaction
export const abandonReservation = ReservationService.abandon
export const resendConfirmationEmail = ReservationService.resendConfirmation
export const deleteReservation = ReservationService.delete
export const getReservationStatus = ReservationService.fetchStatus
export const guestModifyReservation = ReservationService.guestModify
export const validatePromotionCode = PromoCodeService.validate
export const updateAllPromotionCodes = PromoCodeService.updateAll
export const getPromotionCodes = PromoCodeService.fetchAll
export const getCoupons = CouponService.fetchAll
export const searchGuests = GuestService.search
export const getGuest = GuestService.fetchOneDetailed
export const updateGuest = GuestService.update
export const mergeGuest = GuestService.merge
export const getMe = UserService.fetchMe
export const getMeOnboarding = UserService.fetchMeOnboarding
export const login = UserService.login
export const setPassword = UserService.setPassword
export const sendResetPasswordEmail = UserService.sendResetPasswordEmail
export const resetPassword = UserService.resetPassword
export const getUsers = UserService.fetchAll
export const updateAllUsers = UserService.updateAll
export const getReaders = StripeService.fetchReaders
export const cancelReaderAction = StripeService.cancelAction
export const simulatePayment = StripeService.simulatePayment
export const createResidency = ResidencyService.create
export const getResidencies = ResidencyService.fetchAll
export const updateResidency = ResidencyService.update
export const deleteResidency = ResidencyService.delete
export const getResidency = ResidencyService.fetchOnePublic
export const getMailchimpSettings = MailchimpService.fetchSettings
export const updateMailchimpSettings = MailchimpService.updateSettings
export const getMailchimpAudiences = MailchimpService.fetchAudiences
export const disconnectMailchimp = MailchimpService.disconnect
export const createTemplate = MailchimpService.createTemplate
export const getTemplates = NotificationTemplateService.fetchAll
export const updateTemplate = NotificationTemplateService.update
